/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDynamicDestinationList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing Dynamic Destinations list state and data fetching.
    Supports Domain Contextual Filtering pattern.
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import { fetchDynamicDestinations } from '../api/dynamicDestinationsApi';
import { ListDynamicDestinationsParams, DynamicDestination } from '../types';

interface UseDynamicDestinationListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for Dynamic Destinations list management
 * Implements Domain Contextual Filtering pattern
 */
export function useDynamicDestinationList(options: UseDynamicDestinationListOptions = {}) {
  const { domainId, onRefreshNeeded } = options;
  const { showError } = useNotifications();
  const { t } = useI18n();

  const fetchData = useCallback(async (params: Omit<ListDynamicDestinationsParams, 'domainId'>) => {
    try {
      const result = await fetchDynamicDestinations({
        ...params,
        domainId
      });

      const data: DynamicDestination[] = Array.isArray(result.data) ? result.data : [];
      const pagination = result.pagination || { page: 0, limit: 0, total: 0, totalPages: 0 };

      return {
        data,
        pagination: {
          page: pagination.page ?? params.page,
          limit: pagination.limit ?? params.limit,
          total: pagination.total ?? data.length,
          totalPages: pagination.totalPages ?? 0
        }
      };
    } catch (error) {
      showError(
        error?.message
          ? `${t('dynamicDestination.messages.loadError')}: ${error.message}`
          : t('dynamicDestination.messages.loadError'),
      );
      return {
        data: [] as DynamicDestination[],
        pagination: {
          page: 1,
          limit: params.limit,
          total: 0,
          totalPages: 0,
        },
      };
    }
  }, [showError, t, domainId]);

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (domainId !== undefined && onRefreshNeeded) {
      onRefreshNeeded();
    }
  }, [domainId]);

  return {
    fetchData,
    domainId
  };
}

